import type {
  BackplateCalibration,
  BuilderAddon,
  BuilderPartPreviewLayout,
  BuilderStiffnessOption,
  FinishPattern,
  FinishZoneId,
  FrameColourOption,
  FrameGeometry,
  FrameGeometryRanges,
  FrameLayerConfig,
  FrameLogo,
  ImageRegion,
  MmPoint,
  PartCategory,
  PartCategoryId,
  PartFitAdjustment,
  PartImageAnchors,
  PartOptionVariant,
  PartOption,
  PartSubCategory,
  PriceIndicator,
  WheelSize,
} from "@/types";
import { PARTS } from "@/data/parts";
import {
  FRAMES,
  WHEEL_SIZE_OPTIONS,
  defaultGeometryRanges,
} from "@/data/frames";
import {
  DEFAULT_ADDONS,
  DEFAULT_STIFFNESS_OPTIONS,
} from "@/data/builderFunction";
import { DEFAULT_LAYER_ORDER, normaliseLayerOrder } from "@/data/anchorSchemes";
import {
  DEFAULT_LEGAL_DOCS,
  type LegalDoc,
  type LegalSection,
} from "@/data/legal";

/** CMS-editable legal / compliance page. Alias of the LegalDoc content shape. */
export type EditableLegalDoc = LegalDoc;
export type EditableLegalSection = LegalSection;

export const LOCAL_CONTENT_STORAGE_KEY = "burton-bikes-local-content-v1";

export const BUILDER_FRAME_IDS = [
  "raptor-i",
  "felon-i",
  "typhoon-i",
  "raider",
] as const;

export interface EditableHomeContent {
  heroEyebrow: string;
  heroTitle: string;
  heroAccent: string;
  heroSubtitle: string;
  heroImage: string;
  heroCtaText: string;
  heroCtaLink: string;
  aboutEyebrow: string;
  aboutHeading: string;
  aboutSubheading: string;
  aboutBodyOne: string;
  aboutBodyTwo: string;
  aboutImage: string;
  builderTitle: string;
  builderAccent: string;
  builderSubtitle: string;
  builderImage: string;
  builderCtaText: string;
}

export interface EditableBikeModelContent {
  modelName: string;
  tagline: string;
  description: string;
  heroImage: string;
  builderBackplateImage: string;
  galleryImages: string[];
  keySpecs: string[];
  startingPriceText: string;
  enquiryText: string;
  /** The bike's real geometry — drives the scale-true workbench canvas and part alignment. */
  geometry: FrameGeometry;
  /** Per-bike min/max values shown in the rider-facing builder geometry step. */
  geometryRanges: FrameGeometryRanges;
  /** Front wheel sizes offered to riders for this bike model. */
  frontWheelOptions: WheelSize[];
  /** Rear wheel sizes offered to riders for this bike model. */
  rearWheelOptions: WheelSize[];
  /** Axle dots marked on the backplate photo; null until the admin calibrates it. */
  backplateCalibration: BackplateCalibration | null;
  /** Natural width / height of the backplate photo, captured at calibration. */
  backplateAspect: number | null;
  /** Admin-placed bike-side anchors (shock eyes, bar clamp…) in frame mm. */
  customAnchors: Record<string, MmPoint>;
  /** Frame tube image layer — sits under the lugs, recolourable in the builder. */
  frameTubes: FrameLayerConfig;
  /** Lug image layer — laid over the tubes, recolourable independently. */
  frameLugs: FrameLayerConfig;
  /** Tube colours offered for this bike, in display order. */
  tubeColours: FrameColourOption[];
  /** Lug colours offered for this bike, in display order. */
  lugColours: FrameColourOption[];
  /** Sketched areas of the frame render to remove in the builder — e.g. a brake arm baked into the render. */
  frameHideRegions: ImageRegion[];
}

export interface EditableSiteSettings {
  contactEmail: string;
  footerText: string;
  enquiryButtonText: string;
}

export interface EditableBuilderSettings {
  /** When true, the Bars image is treated as the combined bars/grips/headset visual layer. */
  useCombinedCockpitImage: boolean;
}

export interface EditableBuilderPart {
  label: string;
  description: string;
  material: PartOption["material"];
  size: string;
  finish: string;
  priceIndicator: PriceIndicator;
  imageUrl: string;
  /** Pre-transparency-tool image, kept so background removal can be reverted. */
  originalImageUrl: string;
  visible: boolean;
  compatibleFrameIds: string[];
  previewLayouts: Record<string, BuilderPartPreviewLayout>;
  /** Anchor dots drawn on the image in the workbench, keyed by scheme point id. */
  imageAnchors: PartImageAnchors;
  /** Natural width / height of the annotated image. */
  imageAspect: number | null;
  /** Real-world width in mm of the full image, for point-scale schemes. */
  widthMm: number | null;
  /** Rotation in degrees for point-scale schemes. */
  anchorRotationDeg: number;
  /** Two-point parts: swap which mount eye maps to which frame anchor (end-for-end flip). */
  flipEyeToEye: boolean;
  /** Two-point parts: mirror the image across its mount-to-mount axis (e.g. shock reservoir side). */
  flipCentreline: boolean;
  /** Sketched image areas that render behind the frame instead of in front — e.g. the far-side crank arm. */
  backRegions: ImageRegion[];
  /** Per-bike manual corrections after the automatic solve, keyed by frame model id. */
  fitAdjustments: Record<string, PartFitAdjustment>;
  variants?: PartOptionVariant[];
}

export interface EditableBuilderPartAddition extends EditableBuilderPart {
  id: string;
  categoryId: PartCategoryId;
  subCategoryId: string;
  compatibleFrameIds: string[];
}

/** CMS-editable fields of a frame finish option (3D configurator zone finish). */
export interface EditableFinishOption {
  label: string;
  description: string;
  hex: string;
  metalness: number;
  roughness: number;
  clearcoat: number;
  pattern: FinishPattern;
  patternScale: number;
  patternRotationDeg: number;
  /** Uploaded texture (data URL or path) replacing the procedural pattern. */
  textureUrl: string;
  priceDelta: number;
  enabled: boolean;
}

/** An admin-created finish option, appended to a zone's catalog. */
export interface EditableFinishAddition extends EditableFinishOption {
  id: string;
  zone: FinishZoneId;
}

export interface EditableSiteContent {
  home: EditableHomeContent;
  bikeModels: Record<string, EditableBikeModelContent>;
  siteSettings: EditableSiteSettings;
  builderSettings: EditableBuilderSettings;
  builderPartOverrides: Record<string, Partial<EditableBuilderPart>>;
  builderPartAdditions: EditableBuilderPartAddition[];
  /**
   * Catalog/imported parts the admin has deleted from the builder. These parts
   * live in source code so they can't be truly removed — this soft-delete list
   * hides them everywhere and is reversible from the workbench.
   */
  removedBuilderPartIds: string[];
  finishOverrides: Record<string, Partial<EditableFinishOption>>;
  finishAdditions: EditableFinishAddition[];
  /** Frame decals — the whole list is editable (add, edit, delete) in the CMS. */
  frameLogos: FrameLogo[];
  /** Logo colour palette offered to riders — fully editable in the CMS. */
  logoColourOptions: FrameColourOption[];
  /** Carbon tube resin/tint palette offered to riders - fully editable in the CMS. */
  carbonTubeTintColours: FrameColourOption[];
  /** Ride-feel / stiffness options (Function step) — fully editable in the CMS. */
  stiffnessOptions: BuilderStiffnessOption[];
  /** Frame add-ons (Function step) — fully editable in the CMS. */
  builderAddons: BuilderAddon[];
  /** Front-to-back stacking order of preview layers (layer keys, back → front). */
  layerOrder: string[];
  /** Legal / compliance / trust pages, editable in the CMS (Site content → Legal pages). */
  legalDocs: EditableLegalDoc[];
}

/** House logo set — BURTON on the down tube, model name and rider text on the top tube. */
export const DEFAULT_FRAME_LOGOS: FrameLogo[] = [
  {
    id: "logo-brand",
    kind: "brand",
    text: "BURTON",
    tube: "down",
    t: 0.52,
    heightMm: 30,
    rotationDeg: 0,
    useRiderColour: true,
    colourHex: "#C9A84C",
    enabled: true,
  },
  {
    id: "logo-model",
    kind: "model",
    text: "",
    tube: "top",
    t: 0.62,
    heightMm: 16,
    rotationDeg: 0,
    useRiderColour: true,
    colourHex: "#C9A84C",
    enabled: true,
  },
  {
    id: "logo-rider",
    kind: "custom",
    text: "",
    tube: "top",
    t: 0.2,
    heightMm: 9,
    rotationDeg: 0,
    useRiderColour: true,
    colourHex: "#C9A84C",
    enabled: true,
  },
];

export const DEFAULT_LOGO_COLOURS: FrameColourOption[] = [
  { id: "gold", label: "Gold", hex: "#C9A84C" },
  {
    id: "polished-gold",
    label: "Polished Gold",
    hex: "#FFD66B",
    logoFinish: "polished-gold",
  },
  { id: "silver", label: "Silver", hex: "#C0C0C0" },
  {
    id: "polished-silver",
    label: "Polished Silver",
    hex: "#F2F4F7",
    logoFinish: "polished-silver",
  },
  { id: "black", label: "Black", hex: "#141414" },
  { id: "white", label: "White", hex: "#F5F5F5" },
];

export const DEFAULT_CARBON_TUBE_TINT_COLOURS: FrameColourOption[] = [
  { id: "clear", label: "Clear", hex: "#E8E8E4" },
  { id: "smoke", label: "Smoke", hex: "#383838" },
];

const REMOVED_LEGACY_CARBON_TUBE_TINT_IDS = new Set([
  "tinted-green",
  "tinted-gold",
  "black",
]);

const FALLBACK_GEOMETRY: FrameGeometry = {
  reachMm: 470,
  headAngleDeg: 64,
  seatAngleDeg: 77,
  seatAngleActualDeg: 72,
  chainstayMm: 440,
  bbDropMm: 20,
  headTubeMm: 115,
  frontWheel: "29",
  rearWheel: "29",
  forkTravelMm: 160,
  forkLengthMm: 580,
  forkOffsetMm: 44,
  spacerStackMm: 20,
  stemLengthMm: 45,
  stemAngleDeg: 0,
  saddleHeightMm: 760,
  crankLengthMm: 170,
};

export function frameGeometryDefault(frameId: string): FrameGeometry {
  return (
    FRAMES.find((frame) => frame.id === frameId)?.geometryDefaults ??
    FALLBACK_GEOMETRY
  );
}

function defaultWheelOptions(): WheelSize[] {
  return WHEEL_SIZE_OPTIONS.map((option) => option.id);
}

export function emptyFrameLayer(): FrameLayerConfig {
  return {
    imageUrl: "",
    originalImageUrl: "",
    imageAnchors: {},
    imageAspect: null,
  };
}

const LEGACY_MODEL_PLACEHOLDER_IMAGES: Record<string, string> = {
  "felon-i": "/images/burton-tuatara-rs0002.jpg",
  "typhoon-i": "/images/burton-tuatara-rs0008.jpg",
  raider: "/images/burton-tuatara-rs0016.jpg",
};

/** Brand palette starting points — editable per bike in the workbench Frame tab. */
export const DEFAULT_TUBE_COLOURS: FrameColourOption[] = [
  { id: "raw-titanium", label: "Raw Titanium", hex: "#9E9E96" },
  { id: "carbon-black", label: "Carbon Black", hex: "#0A0A0A" },
  { id: "deep-green", label: "Deep Green", hex: "#1A3A2A" },
  { id: "anodised-blue", label: "Anodised Blue", hex: "#1A3A6A" },
  { id: "anodised-copper", label: "Anodised Copper", hex: "#8B4F2A" },
  { id: "polished-silver", label: "Polished Silver", hex: "#E8E8E4" },
];

export const DEFAULT_LUG_COLOURS: FrameColourOption[] = [
  { id: "carbon-black", label: "Carbon Black", hex: "#0A0A0A" },
  { id: "gold", label: "Gold", hex: "#C9A84C" },
  { id: "raw-titanium", label: "Raw Titanium", hex: "#9E9E96" },
  { id: "deep-green", label: "Deep Green", hex: "#1A3A2A" },
];

export const defaultContent: EditableSiteContent = {
  home: {
    heroEyebrow: "The Atelier — Cambridge, New Zealand",
    heroTitle: "Burton",
    heroAccent: "Bikes",
    heroSubtitle:
      "A bicycle without compromise. Conceived around one rider, engineered without constraint, built once.",
    heroImage: "/images/burton-main-hero.png",
    heroCtaText: "Begin your commission",
    heroCtaLink: "/builder",
    aboutEyebrow: "The Marque",
    aboutHeading: "One engineer.",
    aboutSubheading: "One rider. One machine.",
    aboutBodyOne:
      "Burton Bikes was founded on a singular conviction: a machine conceived around one rider - their geometry, their materials, their finish - rides unlike anything the industry can offer. Nothing here is taken from a shelf. Nothing is repeated.",
    aboutBodyTwo:
      "Each frame is designed, engineered and built in Cambridge, New Zealand using advanced small-batch manufacturing - additive-manufactured structures, filament-wound carbon, and optimised forged carbon where they make sense. Six to twelve weeks in the making. Pricing on application. Every commission, one of one.",
    aboutImage: "/images/burton-tuatara-rs0008.jpg",
    builderTitle: "Commission",
    builderAccent: "yours.",
    builderSubtitle:
      "Compose the complete machine - frame, geometry, componentry, finish - down to the final detail. A private consultation follows within 48 hours.",
    builderImage: "/images/raptor/raptor-pagani-lakeside.png",
    builderCtaText: "Enter the atelier",
  },
  bikeModels: {
    "raptor-i": {
      modelName: "Raptor I",
      tagline:
        "Enduro platform. Grade 9 Ti main triangle, carbon rear, machine-learned suspension.",
      description:
        "The Raptor I is the current Burton enduro bike shown across the website. It pairs a triple-butted Grade 9 titanium main triangle with a carbon rear triangle, with suspension kinematics optimised by machine learning around real stage data.",
      heroImage: "/images/raptor/raptor-frame-render.png",
      builderBackplateImage: "",
      galleryImages: [
        "/images/raptor/raptor-porsche-sunset.png",
        "/images/raptor/raptor-frame-render.png",
        "/images/raptor/raptor-pagani-lakeside.png",
      ],
      keySpecs: [
        "Enduro",
        "Grade 9 Titanium",
        "Carbon Rear Triangle",
        "ML-Tuned Suspension",
      ],
      startingPriceText: "Pricing by enquiry",
      enquiryText:
        "Every Raptor is commissioned around your riding, your terrain, and your finish. Enter the atelier to compose yours.",
      geometry: frameGeometryDefault("raptor-i"),
      geometryRanges: defaultGeometryRanges(),
      frontWheelOptions: defaultWheelOptions(),
      rearWheelOptions: defaultWheelOptions(),
      backplateCalibration: null,
      backplateAspect: null,
      customAnchors: {},
      frameTubes: emptyFrameLayer(),
      frameLugs: emptyFrameLayer(),
      tubeColours: [...DEFAULT_TUBE_COLOURS],
      lugColours: [...DEFAULT_LUG_COLOURS],
      frameHideRegions: [],
    },
    "felon-i": {
      modelName: "Felon I",
      tagline:
        "Coming soon. Downhill race platform built for bike-park speed and full-send stability.",
      description:
        "Coming soon. The Felon I is the Burton downhill line: long-travel, race-focused, and tuned for high-speed composure on steep terrain.",
      heroImage: "/images/bike-lines/hidden-felon.png",
      builderBackplateImage: "",
      galleryImages: ["/images/bike-lines/hidden-felon.png"],
      keySpecs: [
        "Downhill",
        "200mm Travel",
        "Carbon Chassis",
        "ML-Tuned Suspension",
      ],
      startingPriceText: "Coming soon",
      enquiryText:
        "Felon I is coming soon. Enquire to register interest and follow release availability.",
      geometry: frameGeometryDefault("felon-i"),
      geometryRanges: defaultGeometryRanges(),
      frontWheelOptions: defaultWheelOptions(),
      rearWheelOptions: defaultWheelOptions(),
      backplateCalibration: null,
      backplateAspect: null,
      customAnchors: {},
      frameTubes: emptyFrameLayer(),
      frameLugs: emptyFrameLayer(),
      tubeColours: [...DEFAULT_TUBE_COLOURS],
      lugColours: [...DEFAULT_LUG_COLOURS],
      frameHideRegions: [],
    },
    "typhoon-i": {
      modelName: "Typhoon I",
      tagline:
        "Coming soon. Down-country platform, fast, efficient, and ready for rough technical days.",
      description:
        "Coming soon. The Typhoon I is the Burton down-country line: lighter, sharper, and more efficient than the enduro bikes while still built around real trail control.",
      heroImage: "/images/bike-lines/hidden-typhoon.png",
      builderBackplateImage: "",
      galleryImages: ["/images/bike-lines/hidden-typhoon.png"],
      keySpecs: [
        "Down-Country",
        "Lightweight Carbon",
        "120-140mm Travel",
        "ML-Tuned Suspension",
      ],
      startingPriceText: "Coming soon",
      enquiryText:
        "Typhoon I is coming soon. Enquire to register interest and follow release availability.",
      geometry: frameGeometryDefault("typhoon-i"),
      geometryRanges: defaultGeometryRanges(),
      frontWheelOptions: defaultWheelOptions(),
      rearWheelOptions: defaultWheelOptions(),
      backplateCalibration: null,
      backplateAspect: null,
      customAnchors: {},
      frameTubes: emptyFrameLayer(),
      frameLugs: emptyFrameLayer(),
      tubeColours: [...DEFAULT_TUBE_COLOURS],
      lugColours: [...DEFAULT_LUG_COLOURS],
      frameHideRegions: [],
    },
    raider: {
      modelName: "Raider",
      tagline:
        "Coming soon. E-Enduro platform with Burton Kinetics suspension tuned for the mass it carries.",
      description:
        "Coming soon. The Raider is the Burton E-Enduro line: the same custom-fit thinking as the Raptor I, with motor-assisted range for bigger days and repeated descents.",
      heroImage: "/images/bike-lines/hidden-raider.png",
      builderBackplateImage: "",
      galleryImages: ["/images/bike-lines/hidden-raider.png"],
      keySpecs: [
        "E-Enduro",
        "Carbon Chassis",
        "170mm Travel",
        "ML-Tuned Suspension",
      ],
      startingPriceText: "Coming soon",
      enquiryText:
        "Raider is coming soon. Enquire to register interest and follow release availability.",
      geometry: frameGeometryDefault("raider"),
      geometryRanges: defaultGeometryRanges(),
      frontWheelOptions: defaultWheelOptions(),
      rearWheelOptions: defaultWheelOptions(),
      backplateCalibration: null,
      backplateAspect: null,
      customAnchors: {},
      frameTubes: emptyFrameLayer(),
      frameLugs: emptyFrameLayer(),
      tubeColours: [...DEFAULT_TUBE_COLOURS],
      lugColours: [...DEFAULT_LUG_COLOURS],
      frameHideRegions: [],
    },
  },
  siteSettings: {
    contactEmail: "kieran@burtonbikes.co.nz",
    footerText: "Burton Bikes Ltd. All rights reserved.",
    enquiryButtonText: "Enquire",
  },
  builderSettings: {
    useCombinedCockpitImage: true,
  },
  builderPartOverrides: {},
  builderPartAdditions: [],
  removedBuilderPartIds: [],
  finishOverrides: {},
  finishAdditions: [],
  frameLogos: DEFAULT_FRAME_LOGOS.map((logo) => ({ ...logo })),
  logoColourOptions: DEFAULT_LOGO_COLOURS.map((colour) => ({ ...colour })),
  carbonTubeTintColours: DEFAULT_CARBON_TUBE_TINT_COLOURS.map((colour) => ({
    ...colour,
  })),
  stiffnessOptions: DEFAULT_STIFFNESS_OPTIONS.map((option) => ({ ...option })),
  builderAddons: DEFAULT_ADDONS.map((addon) => ({ ...addon })),
  layerOrder: [...DEFAULT_LAYER_ORDER],
  legalDocs: DEFAULT_LEGAL_DOCS.map((doc) => ({
    ...doc,
    sections: doc.sections.map((section) => ({ ...section })),
  })),
};

function mergeBikeModels(
  incoming: Partial<EditableSiteContent> | null | undefined,
): Record<string, EditableBikeModelContent> {
  const incomingModels = incoming?.bikeModels ?? {};
  return Object.fromEntries(
    Object.entries(defaultContent.bikeModels).map(([id, model]) => {
      const stored = incomingModels[id];
      // Geometry is deep-merged so content saved before a field existed still
      // inherits that field's default rather than dropping it.
      const geometry = { ...model.geometry, ...(stored?.geometry ?? {}) };
      const geometryRanges = {
        ...model.geometryRanges,
        ...(stored?.geometryRanges ?? {}),
      };
      const legacyHero = LEGACY_MODEL_PLACEHOLDER_IMAGES[id];
      const heroImage =
        stored?.heroImage === legacyHero
          ? model.heroImage
          : (stored?.heroImage ?? model.heroImage);
      const galleryImages =
        stored?.galleryImages?.length === 0 && stored?.heroImage === legacyHero
          ? model.galleryImages
          : (stored?.galleryImages ?? model.galleryImages);

      return [
        id,
        {
          ...model,
          ...(stored ?? {}),
          heroImage,
          galleryImages,
          geometry,
          geometryRanges,
          frontWheelOptions:
            stored?.frontWheelOptions ?? model.frontWheelOptions,
          rearWheelOptions: stored?.rearWheelOptions ?? model.rearWheelOptions,
        },
      ];
    }),
  );
}

function normaliseBuilderPart(
  part: Partial<EditableBuilderPart> | undefined,
): Partial<EditableBuilderPart> {
  if (!part) return {};
  const normalised: Partial<EditableBuilderPart> = {
    ...part,
    previewLayouts: part.previewLayouts ?? {},
    variants: part.variants ?? [],
  };
  if (part.compatibleFrameIds)
    normalised.compatibleFrameIds = part.compatibleFrameIds;
  return normalised;
}

function normaliseBuilderPartAddition(
  part: Partial<EditableBuilderPartAddition>,
): EditableBuilderPartAddition {
  return {
    id: part.id ?? `custom-${Date.now()}`,
    categoryId: part.categoryId ?? "forks",
    subCategoryId: part.subCategoryId ?? "",
    label: part.label ?? "New builder option",
    description: part.description ?? "",
    material: part.material ?? "alloy",
    size: part.size ?? "",
    finish: part.finish ?? "",
    priceIndicator: part.priceIndicator ?? "upgrade",
    imageUrl: part.imageUrl ?? "",
    originalImageUrl: part.originalImageUrl ?? "",
    visible: part.visible ?? true,
    compatibleFrameIds: part.compatibleFrameIds ?? [...BUILDER_FRAME_IDS],
    previewLayouts: part.previewLayouts ?? {},
    imageAnchors: part.imageAnchors ?? {},
    imageAspect: part.imageAspect ?? null,
    widthMm: part.widthMm ?? null,
    anchorRotationDeg: part.anchorRotationDeg ?? 0,
    flipEyeToEye: part.flipEyeToEye ?? false,
    flipCentreline: part.flipCentreline ?? false,
    backRegions: part.backRegions ?? [],
    fitAdjustments: part.fitAdjustments ?? {},
    variants: part.variants ?? [],
  };
}

function normaliseFinishAddition(
  finish: Partial<EditableFinishAddition>,
): EditableFinishAddition {
  return {
    id: finish.id ?? `custom-finish-${Date.now()}`,
    zone: finish.zone ?? "lugs",
    label: finish.label ?? "New finish",
    description: finish.description ?? "",
    hex: finish.hex ?? "#FFFFFF",
    metalness: finish.metalness ?? 0,
    roughness: finish.roughness ?? 0.5,
    clearcoat: finish.clearcoat ?? 0,
    pattern: finish.pattern ?? "none",
    patternScale: finish.patternScale ?? 1,
    patternRotationDeg: finish.patternRotationDeg ?? 0,
    textureUrl: finish.textureUrl ?? "",
    priceDelta: finish.priceDelta ?? 0,
    enabled: finish.enabled ?? true,
  };
}

function normaliseFrameLogo(logo: Partial<FrameLogo>): FrameLogo {
  return {
    id: logo.id ?? `logo-${Date.now()}`,
    kind: logo.kind ?? "brand",
    text: logo.text ?? "",
    tube: logo.tube ?? "down",
    t: typeof logo.t === "number" ? logo.t : 0.5,
    heightMm: typeof logo.heightMm === "number" ? logo.heightMm : 20,
    rotationDeg: typeof logo.rotationDeg === "number" ? logo.rotationDeg : 0,
    faceOffsetDeg:
      typeof logo.faceOffsetDeg === "number" ? logo.faceOffsetDeg : 0,
    useRiderColour: logo.useRiderColour ?? true,
    colourHex: logo.colourHex ?? "#C9A84C",
    enabled: logo.enabled ?? true,
  };
}

function normaliseLogoColourOption(
  colour: Partial<FrameColourOption>,
): FrameColourOption {
  return {
    id: colour.id ?? `logo-colour-${Date.now()}`,
    label: colour.label ?? "New colour",
    hex: colour.hex ?? "#C9A84C",
    logoFinish: colour.logoFinish ?? "flat",
  };
}

function normaliseLogoColourOptions(
  colours: Partial<FrameColourOption>[] | undefined,
): FrameColourOption[] {
  if (colours) return colours.map(normaliseLogoColourOption);
  return DEFAULT_LOGO_COLOURS.map((colour) => ({
    ...colour,
    logoFinish: colour.logoFinish ?? "flat",
  }));
}

function normaliseCarbonTubeTintOption(
  colour: Partial<FrameColourOption>,
): FrameColourOption {
  return {
    id: colour.id ?? `carbon-tint-${Date.now()}`,
    label: colour.label ?? "New tint",
    hex: colour.hex ?? "#1A3A2A",
    logoFinish: "flat",
  };
}

function normaliseCarbonTubeTintOptions(
  colours: Partial<FrameColourOption>[] | undefined,
): FrameColourOption[] {
  if (colours)
    return colours
      .map(normaliseCarbonTubeTintOption)
      .filter((colour) => !REMOVED_LEGACY_CARBON_TUBE_TINT_IDS.has(colour.id));
  return DEFAULT_CARBON_TUBE_TINT_COLOURS.map((colour) => ({
    ...colour,
    logoFinish: "flat",
  }));
}

function normaliseStiffnessOption(
  option: Partial<BuilderStiffnessOption>,
): BuilderStiffnessOption {
  return {
    id: option.id ?? `stiffness-${Date.now()}`,
    label: option.label ?? "New ride feel",
    description: option.description ?? "",
    imageUrl: option.imageUrl ?? "",
    price: typeof option.price === "number" ? option.price : 0,
    enabled: option.enabled ?? true,
  };
}

function normaliseAddon(addon: Partial<BuilderAddon>): BuilderAddon {
  return {
    id: addon.id ?? `addon-${Date.now()}`,
    label: addon.label ?? "New add-on",
    description: addon.description ?? "",
    imageUrl: addon.imageUrl ?? "",
    price: typeof addon.price === "number" ? addon.price : 0,
    enabled: addon.enabled ?? true,
  };
}

function normaliseLegalDocs(
  incoming: EditableLegalDoc[] | undefined,
): EditableLegalDoc[] {
  const stored = new Map((incoming ?? []).map((doc) => [doc.slug, doc]));
  // Keep the canonical set/order from source, letting saved edits win per page.
  // A stored page can override title/intro/sections; new default pages appear
  // automatically after a deploy.
  return DEFAULT_LEGAL_DOCS.map((base) => {
    const override = stored.get(base.slug);
    if (!override)
      return { ...base, sections: base.sections.map((s) => ({ ...s })) };
    return {
      slug: base.slug,
      eyebrow: override.eyebrow ?? base.eyebrow,
      title: override.title ?? base.title,
      intro: override.intro ?? base.intro,
      sections: Array.isArray(override.sections)
        ? override.sections.map((section) => ({
            heading: section?.heading ?? "",
            body: section?.body ?? "",
          }))
        : base.sections.map((s) => ({ ...s })),
    };
  });
}

export function normaliseContent(
  incoming: Partial<EditableSiteContent> | null | undefined,
): EditableSiteContent {
  return {
    home: { ...defaultContent.home, ...(incoming?.home ?? {}) },
    bikeModels: mergeBikeModels(incoming),
    siteSettings: {
      ...defaultContent.siteSettings,
      ...(incoming?.siteSettings ?? {}),
    },
    builderSettings: {
      ...defaultContent.builderSettings,
      ...(incoming?.builderSettings ?? {}),
    },
    builderPartOverrides: Object.fromEntries(
      Object.entries(incoming?.builderPartOverrides ?? {}).map(([id, part]) => [
        id,
        normaliseBuilderPart(part),
      ]),
    ),
    builderPartAdditions: (incoming?.builderPartAdditions ?? []).map(
      normaliseBuilderPartAddition,
    ),
    removedBuilderPartIds: incoming?.removedBuilderPartIds ?? [],
    finishOverrides: incoming?.finishOverrides ?? {},
    finishAdditions: (incoming?.finishAdditions ?? []).map(
      normaliseFinishAddition,
    ),
    // Seed defaults only when the key is absent — an admin deleting every
    // entry is a valid state that must survive reloads.
    frameLogos: incoming?.frameLogos
      ? incoming.frameLogos.map(normaliseFrameLogo)
      : DEFAULT_FRAME_LOGOS.map((logo) => ({ ...logo })),
    logoColourOptions: normaliseLogoColourOptions(incoming?.logoColourOptions),
    carbonTubeTintColours: normaliseCarbonTubeTintOptions(
      incoming?.carbonTubeTintColours,
    ),
    // Seed defaults only when the key is absent — an admin deleting every entry
    // is a valid state that must survive reloads.
    stiffnessOptions: incoming?.stiffnessOptions
      ? incoming.stiffnessOptions.map(normaliseStiffnessOption)
      : DEFAULT_STIFFNESS_OPTIONS.map((option) => ({ ...option })),
    builderAddons: incoming?.builderAddons
      ? incoming.builderAddons.map(normaliseAddon)
      : DEFAULT_ADDONS.map((addon) => ({ ...addon })),
    // Reconcile against the current slot list so new/removed part layers stay placed.
    layerOrder: normaliseLayerOrder(incoming?.layerOrder),
    legalDocs: normaliseLegalDocs(incoming?.legalDocs),
  };
}

export interface FlattenedBuilderOption {
  category: PartCategory;
  subCategory: PartSubCategory | null;
  option: PartOption;
}

export function flattenBuilderOptions(
  parts: PartCategory[] = PARTS,
): FlattenedBuilderOption[] {
  return parts.flatMap<FlattenedBuilderOption>((category) => {
    if (category.subcategories?.length) {
      return category.subcategories.flatMap((subCategory) =>
        subCategory.options.map<FlattenedBuilderOption>((option) => ({
          category,
          subCategory,
          option,
        })),
      );
    }

    return category.options.map<FlattenedBuilderOption>((option) => ({
      category,
      subCategory: null,
      option,
    }));
  });
}
